import { OpenAPI as OpenAPIv3_0 } from '@criteria/openapi/v3.0'
import {
  AuthorizationCodeOAuthFlow,
  ImplicitOAuthFlow,
  OpenAPI as OpenAPIv3_1,
  SecurityScheme
} from '@criteria/openapi/v3.1'
import { CogIcon } from '@heroicons/react/24/outline'
import clsx from 'clsx'
import {
  Badge,
  Button,
  Dialog,
  DialogActions,
  DialogBody,
  DialogTitle,
  Field,
  FieldGroup,
  Fieldset,
  Form,
  FormDialog,
  Input,
  Inset,
  Label,
  Legend,
  Pane,
  Scroll,
  Select,
  Text
} from '@criteria-labs/react'
import { useRouter } from 'next/navigation'
import { Fragment, useState } from 'react'
import { ServerPicker } from './ServerPicker'
import { useAuthorization } from './authorization-store'
import { ConsoleTab } from '../console/ConsoleLayout'
import { useAPIExplorer } from './APIExplorerProvider'

type OpenAPI = OpenAPIv3_1<never> | OpenAPIv3_0<never>
type Server = { url: string }

export function APIExplorerSettingsButton({ selected, onClick }: { selected: boolean; onClick?: () => void }) {
  return (
    <ConsoleTab aria-label="Settings" selected={selected} onClick={onClick}>
      <CogIcon className="size-5" />
    </ConsoleTab>
  )
}

function APIKeyAuthorization({
  namespace,
  securitySchemeName,
  securityScheme
}: {
  namespace: string
  securitySchemeName: string
  securityScheme: SecurityScheme & { type: 'apiKey' }
}) {
  const [open, setOpen] = useState(false)
  const [authorization, setAuthorization] = useAuthorization(namespace, securitySchemeName)
  return (
    <>
      <Label className="col-start-1">API Key</Label>
      {authorization && (
        <Badge className="flex" color="green">
          <svg className={clsx('h-2 w-2 fill-green-500')} viewBox="0 0 6 6" aria-hidden="true">
            <circle cx={3} cy={3} r={3} />
          </svg>
          <span className="flex-1 text-center">Configured</span>
        </Badge>
      )}
      {!authorization && (
        <Badge className="flex">
          <svg className={clsx('h-2 w-2 fill-zinc-500')} viewBox="0 0 6 6" aria-hidden="true">
            <circle cx={3} cy={3} r={3} />
          </svg>
          <span className="flex-1 text-center">Not configured</span>
        </Badge>
      )}
      <Button outline className="text-xs" onClick={() => setOpen(true)}>
        Configure
      </Button>
      <FormDialog
        open={open}
        onClose={setOpen}
        title="Configure API Key Authorization"
        description={securityScheme.description}
        submitActionTitle="Save"
        submittingActionTitle="Saving&hellip;"
        color="indigo"
        defaultValues={{ apiKey: '' }}
        schema={{
          type: 'object',
          required: ['apiKey'],
          properties: {
            apiKey: { title: 'API Key', type: 'string' }
          }
        }}
        onSubmit={async ({ apiKey }) => {
          setAuthorization(apiKey)
        }}
      />
    </>
  )
}

function HTTPAuthorization({
  namespace,
  securitySchemeName,
  securityScheme
}: {
  namespace: string
  securitySchemeName: string
  securityScheme: SecurityScheme & { type: 'http' }
}) {
  const [open, setOpen] = useState(false)
  const [authorization, setAuthorization] = useAuthorization(namespace, securitySchemeName)
  return (
    <>
      <Label className="col-start-1">HTTP</Label>
      {authorization && (
        <Badge className="flex" color="green">
          <svg className={clsx('h-2 w-2 fill-green-500')} viewBox="0 0 6 6" aria-hidden="true">
            <circle cx={3} cy={3} r={3} />
          </svg>
          <span className="flex-1 text-center">Configured</span>
        </Badge>
      )}
      {!authorization && (
        <Badge className="flex">
          <svg className={clsx('h-2 w-2 fill-zinc-500')} viewBox="0 0 6 6" aria-hidden="true">
            <circle cx={3} cy={3} r={3} />
          </svg>
          <span className="flex-1 text-center">Not configured</span>
        </Badge>
      )}
      <Button outline className="text-xs" onClick={() => setOpen(true)}>
        Configure
      </Button>
      <FormDialog
        open={open}
        onClose={setOpen}
        title="Configure HTTP Authorization"
        description={securityScheme.description}
        submitActionTitle="Save"
        submittingActionTitle="Saving&hellip;"
        color="indigo"
        defaultValues={{ token: '' }}
        schema={{
          type: 'object',
          required: ['token'],
          properties: {
            token: { title: 'Token', type: 'string' }
          }
        }}
        onSubmit={async ({ token }) => {
          setAuthorization(token)
        }}
      />
    </>
  )
}

function OAuth2Authorization({
  namespace,
  securitySchemeName,
  securityScheme
}: {
  namespace: string
  securitySchemeName: string
  securityScheme: SecurityScheme & { type: 'oauth2' }
}) {
  const { push } = useRouter()
  const [open, setOpen] = useState(false)
  const [authorization, setAuthorization] = useAuthorization(namespace, securitySchemeName)
  return (
    <>
      <Label className="col-start-1">OAuth 2.0</Label>
      {authorization && (
        <Badge className="flex" color="green">
          <svg className={clsx('h-2 w-2 fill-green-500')} viewBox="0 0 6 6" aria-hidden="true">
            <circle cx={3} cy={3} r={3} />
          </svg>
          <span className="flex-1 text-center">Configured</span>
        </Badge>
      )}
      {!authorization && (
        <Badge className="flex">
          <svg className={clsx('h-2 w-2 fill-zinc-500')} viewBox="0 0 6 6" aria-hidden="true">
            <circle cx={3} cy={3} r={3} />
          </svg>
          <span className="flex-1 text-center">Not configured</span>
        </Badge>
      )}
      {/* <Button outline className="text-xs" onClick={() => setOpen(true)}>
        Configure
      </Button> */}
      <Dialog open={open} onClose={setOpen}>
        <Form
          defaultValues={{
            flow: Object.keys(securityScheme.flows ?? {}).length > 0 ? Object.keys(securityScheme.flows)[0] : '',
            clientID: ''
          }}
          onSubmit={async ({ flow, clientID }) => {
            switch (flow) {
              case 'implicit':
                const implicitFlow = (securityScheme.flows ?? {}).implicit as ImplicitOAuthFlow | undefined
                if (!implicitFlow?.authorizationUrl) {
                  return false
                }
                push(
                  `${implicitFlow.authorizationUrl}?response_type=token&client_id=${clientID}&redirect_uri=${window.location}`
                )
                break
              case 'authorizationCode':
                const authorizationCodeFlow = (securityScheme.flows ?? {}).authorizationCode as
                  | AuthorizationCodeOAuthFlow
                  | undefined
                if (!authorizationCodeFlow?.authorizationUrl) {
                  return false
                }
                push(
                  `${authorizationCodeFlow.authorizationUrl}?response_type=code&client_id=${clientID}&redirect_uri=${window.location}`
                )
                break
            }
          }}
        >
          {({ watch }) => (
            <>
              <DialogTitle>Configure OAuth 2.0 Authorization</DialogTitle>
              {securityScheme.description && <Text>{securityScheme.description}</Text>}
              <DialogBody>
                <Fieldset>
                  <FieldGroup>
                    <Field>
                      <Label>Flow</Label>
                      <Select name="flow">
                        <option value="" disabled>
                          Choose a flow&hellip;
                        </option>
                        {Object.keys(securityScheme.flows).map((flow) => (
                          <option key={flow} value={flow}>
                            {flow}
                          </option>
                        ))}
                      </Select>
                    </Field>
                  </FieldGroup>
                  {watch('flow') === 'implicit' && (
                    <FieldGroup>
                      <Field>
                        <Label>Client ID</Label>
                        <Input name="clientID" />
                      </Field>
                    </FieldGroup>
                  )}
                  {watch('flow') === 'authorizationCode' && (
                    <FieldGroup>
                      <Field>
                        <Label>Client ID</Label>
                        <Input name="clientID" />
                      </Field>
                    </FieldGroup>
                  )}
                </Fieldset>
              </DialogBody>
              <DialogActions>
                <Button plain onClick={() => setOpen(false)}>
                  Cancel
                </Button>
                <Button type="submit" color="indigo">
                  Authorize
                </Button>
              </DialogActions>
            </>
          )}
        </Form>
      </Dialog>
    </>
  )
}

function OpenIDConnectAuthorization({
  namespace,
  securitySchemeName,
  securityScheme
}: {
  namespace: string
  securitySchemeName: string
  securityScheme: SecurityScheme & { type: 'openIdConnect' }
}) {
  const { push } = useRouter()
  const [open, setOpen] = useState(false)
  const [authorization, setAuthorization] = useAuthorization(namespace, securitySchemeName)
  return (
    <>
      <Label className="col-start-1">OAuth 2.0</Label>
      {authorization && (
        <Badge className="flex" color="green">
          <svg className={clsx('h-2 w-2 fill-green-500')} viewBox="0 0 6 6" aria-hidden="true">
            <circle cx={3} cy={3} r={3} />
          </svg>
          <span className="flex-1 text-center">Configured</span>
        </Badge>
      )}
      {!authorization && (
        <Badge className="flex">
          <svg className={clsx('h-2 w-2 fill-zinc-500')} viewBox="0 0 6 6" aria-hidden="true">
            <circle cx={3} cy={3} r={3} />
          </svg>
          <span className="flex-1 text-center">Not configured</span>
        </Badge>
      )}
      {/* <Button outline className="text-xs" onClick={() => setOpen(true)}>
        Configure
      </Button> */}
    </>
  )
}

export function APIExplorerSettings() {
  const { namespace, openAPI, server, setServer, operation, setOperation } = useAPIExplorer()
  if (!openAPI) {
    return (
      <Pane>
        <Inset className="flex h-full w-full items-center justify-center">
          <Text>Select an OpenAPI document to begin.</Text>
        </Inset>
      </Pane>
    )
  }
  return (
    <Pane background="secondary" className="absolute inset-0">
      <Scroll>
        <Inset>
          <div className="space-y-4">
            <div className="rounded-lg bg-zinc-100 p-3 dark:bg-zinc-800">
              <Fieldset>
                <Field>
                  <Label>Server</Label>
                  <ServerPicker openAPI={openAPI} value={server} onChange={setServer} />
                </Field>
              </Fieldset>
            </div>
            <div className="rounded-lg bg-zinc-100 p-3 dark:bg-zinc-800">
              <Fieldset>
                <Legend>Authorization</Legend>
                <Text>This API requires authorization using the following schemes.</Text>
                <div data-slot="control">
                  <div className="inline-grid grid-cols-3 items-center gap-x-3 gap-y-3">
                    {Object.entries(openAPI.components?.securitySchemes ?? {}).map(([name, securityScheme]) => {
                      return (
                        <Fragment key={name}>
                          {securityScheme.type === 'apiKey' && (
                            <APIKeyAuthorization
                              namespace={namespace}
                              securityScheme={securityScheme}
                              securitySchemeName={name}
                            />
                          )}
                          {securityScheme.type === 'http' && (
                            <HTTPAuthorization
                              namespace={namespace}
                              securityScheme={securityScheme}
                              securitySchemeName={name}
                            />
                          )}
                          {securityScheme.type === 'oauth2' && (
                            <OAuth2Authorization
                              namespace={namespace}
                              securityScheme={securityScheme}
                              securitySchemeName={name}
                            />
                          )}
                          {securityScheme.type === 'openIdConnect' && (
                            <OpenIDConnectAuthorization
                              namespace={namespace}
                              securityScheme={securityScheme}
                              securitySchemeName={name}
                            />
                          )}
                        </Fragment>
                      )
                    })}
                  </div>
                </div>
              </Fieldset>
            </div>
          </div>
        </Inset>
      </Scroll>
    </Pane>
  )
}
